<template>
  <wdw-dynbox :title="'Feedbackfragen'" :config="{ showControls: true }" :minimize="!expanded" v-if="canShowStats">
    <TabView>
      <TabPanel header="Text-Darstellung">
        <div
          v-for="attribute in calculatedResponseStatistic"
          v-bind:key="attribute.id"
          class="wdw-feedback-attribute"
          role="listitem"
        >
          <p class="response-statistic-title">{{ attribute.name }}</p>
          <p v-for="stats in attribute.statistics" v-bind:key="stats.title" class="response-statistic-value">
            <b>{{ stats.title }}</b>
            : {{ stats.value }}
          </p>
        </div>
      </TabPanel>
      <TabPanel header="Diagramm-Darstellung">
        <div
          v-for="chartData in chartDataArray"
          v-bind:key="chartData.id"
          class="wdw-feedback-attribute"
          role="listitem"
        >
          <h4>{{ chartData.name }}</h4>
          <vertical-bar-chart
            :chartData="chartData.data"
            :height="chartData.height"
            :styles="{ height: chartData.height + 'px' }"
            :options="{ ticks: { precision: 0 } }"
          />
        </div>
      </TabPanel>
    </TabView>
  </wdw-dynbox>
</template>
<script>
import { computed } from '@vue/runtime-core';
import { toRefs } from '@vue/reactivity';

import WdwDynbox from '@/components/general/WdwDynbox.vue';
import VerticalBarChart from '@/components/general/Charts/VerticalBarChart.vue';

import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

import useTopicConfig from '@/composables/topic/useTopicConfig';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import useConverter from '@/composables/general/useConverter';

// TODO
// [] -> get topic using topicid

export default {
  name: 'FeedbackResponseStats',

  props: {
    thought: {
      type: Object,
      required: true,
    },
    topic: {
      type: Object,
      required: true,
    },
    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['feedback-form-close'],

  components: {
    WdwDynbox,
    VerticalBarChart,
    TabView,
    TabPanel,
  },

  setup(props, context) {
    const { thought, topic } = toRefs(props);
    const {
      canFeedbackOnThoughts,
      canFeedbackAfterLogin,
      // canEditFeedback,
    } = useTopicConfig();
    const { toBool } = useConverter();

    const feedback = computed(() => {
      return topic.value ? topic.value.feedback : null;
    });
    const responseStatistic = computed(() => {
      return thought.value ? thought.value.feedback_response_stats : null;
    });
    const relevantFeedbackAttributes = computed(() => {
      return topic.value.feedback.attributes.filter(attr => {
        if (attr.type == 'select' || attr.type == 'checkbox' || attr.type == 'valuelist') {
          return true;
        }
      });
    });
    const calculatedResponseStatistic = computed(() => {
      const statsArray = [];
      if (!responseStatistic.value) {
        return statsArray;
      }
      relevantFeedbackAttributes.value.forEach(attr => {
        const result_stats = [];
        const attrStats = responseStatistic.value.by_attribute[attr.id];
        for (const [key, value] of Object.entries(attrStats)) {
          let title_value = key;
          if ((attr.type == 'select' || attr.type == 'valuelist') && attr.values.length > 0) {
            const value = attr.values.find(val => val.id === title_value);
            title_value = value ? value.text : title_value;
            // get human readable title
          } else if (attr.type == 'checkbox') {
            if (toBool(title_value)) {
              title_value = 'Ja';
            } else {
              title_value = 'Nein';
            }
          }
          result_stats.push({
            title: title_value,
            value: attrStats[key],
          });
        }
        // if(attrStats && attrStats.keys.length > 0){
        //     attrStats.keys.forEach((key) => {
        //     })
        // }
        statsArray.push({
          id: attr.id,
          name: attr.name,
          statistics: result_stats,
        });
      });
      return statsArray;
    });
    const canShowStats = computed(() => {
      return (
        calculatedResponseStatistic.value.length > 0 &&
        responseStatistic.value && // statistic exists
        feedback.value != null && // Feedback exists
        (feedback.value.displayfeedback === 'always' || // Always display the feedback
          // or display after feedback is not active anymire
          (!feedback.value.active && topic.value.feedback.displayfeedback == 'afterfeedback'))
      );
    });
    const chartDataArray = computed(() => {
      const chartDataArray = [];
      if (!responseStatistic.value) {
        return chartDataArray;
      }
      relevantFeedbackAttributes.value.forEach(attr => {
        const attrStats = responseStatistic.value.by_attribute[attr.id];

        // Sort the data by its keys
        const chartDataOrdered = Object.keys(attrStats)
          .sort()
          .reduce((obj, key) => {
            obj[key] = attrStats[key];
            return obj;
          }, {});

        // get the labels from response
        let labels = Object.keys(chartDataOrdered);
        if (labels.length == 0) {
          // No data
        } else {
          // statistic data exists
          // Update labels to human-readable names
          labels = labels.map(label => {
            if ((attr.type == 'select' || attr.type == 'valuelist') && attr.values.length > 0) {
              const value = attr.values.find(val => val.id === label);
              return value ? value.text : label;
            } else if (attr.type == 'checkbox') {
              if (toBool(label)) {
                return 'Ja';
              } else {
                return 'Nein';
              }
            }
            return label;
          });
          // push to the chart data array
          chartDataArray.push({
            id: attr.id,
            name: attr.name,
            height: labels.length * 75 + 20,
            data: {
              labels: labels,
              datasets: [
                {
                  label: attr.name,
                  backgroundColor: [
                    '#3F91A0',
                    '#893FAF',
                    '#7286A0',
                    '#A3BFA8',
                    '#CDE7B0',
                    '#BE6E46',
                    '#BFD7EA',
                    '#C81D25',
                    '#FF5A5F',
                    '#087E8B',
                    '#0B3954',
                    '#8BAAAD',
                    '#F4FFF8',
                    '#4D4847',
                    '#1C3738',
                    '#000F08',
                    '#3F91A0',
                    '#7A918D',
                    '#A18276',
                    '#736372',
                    '#AAC0AA',
                    '#DCEED1',
                  ],
                  data: Object.values(chartDataOrdered),
                },
              ],
            },
          });
        }
      });
      return chartDataArray;
    });

    return {
      // Methods
      canFeedbackOnThoughts,
      canFeedbackAfterLogin,

      // Computed
      canShowStats,
      responseStatistic,
      calculatedResponseStatistic,
      relevantFeedbackAttributes,
      chartDataArray,

      // VARS
    };
  },
};
</script>
<style scoped>
.wdw-feedback-attribute p {
  margin-bottom: 0.5rem;
}
p.response-statistic-title {
  font-size: 1rem;
}
p.response-statistic-value {
  font-size: 0.8rem;
}
</style>
